import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import io from "socket.io-client";
import Axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrown,
  faDroplet,
  faEye,
  faLeaf,
} from "@fortawesome/free-solid-svg-icons";

import ChatBox from "./ChatBox";
import ColorPicker from "./ColorPicker";
import BanneredDiv from "./BanneredDiv";
import Timer from "./Timer";
import WinningMessage from "./WinningMessage";
import PlayerControlsModal from "./PlayerControlsModal";

import { baseUrl } from "../config";
import themes from "../themes";

const Game = () => {
  const { code } = useParams();
  const [socket, setSocket] = useState(null);
  const [username, setUsername] = useState("");
  const [teams, setTeams] = useState([]);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [word, setWord] = useState(null);
  const [bannedWords, setBannedWords] = useState([]);
  const [, setCategory] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [wordlists, setWordlists] = useState(null);
  const [speaker, setSpeaker] = useState(null);
  const [canSeeWord, setCanSeeWord] = useState(false);
  const [otherTeam, setOtherTeam] = useState([]);
  const [timePerRound, setTimePerRound] = useState(0);
  const [isRoundStarted, setIsRoundStarted] = useState(false);
  const [speakerTeam, setSpeakerTeam] = useState(null);
  const [missed, setMissed] = useState(0);
  const [guessed, setGuessed] = useState(0);
  const [gameStartedLockout, setGameStartedLockout] = useState(null);
  const [roundsToComplete, setRoundsToComplete] = useState(0);
  const [roundsCompleted, setRoundsCompleted] = useState(0);
  const [isGameEnded, setIsGameEnded] = useState(false);
  const [postRoundString, setPostRoundString] = useState("");
  const [individualScores, setIndividualScores] = useState([]);
  const [hasSetUsername, setHasSetUsername] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [modalStyle, setModalStyle] = useState({});
  const [lobbyMissing, setLobbyMissing] = useState(false);
  const [startError, setStartError] = useState("");
  const [copied, setCopied] = useState(false);
  const [isMuted, setIsMuted] = useState(
    () => localStorage.getItem("outlawMuted") === "1"
  );
  const [connectionState, setConnectionState] = useState("connected");
  const [spectators, setSpectators] = useState([]);
  const [actionError, setActionError] = useState("");
  const isGameStartedRef = useRef(false);
  const [activeTheme, setActiveTheme] = useState(
    () => localStorage.getItem("outlawThemeId") || "grey"
  );
  const [settings, setSettings] = useState({
    pack: "General",
    time: "90",
    losePoint: "Yes",
  });

  useEffect(() => {
    const cachedUsername = getUsernameCache();
    if (cachedUsername) setUsername((current) => current || cachedUsername);
  }, []);

  useEffect(() => {
    isGameStartedRef.current = isGameStarted;
  }, [isGameStarted]);

  // Refreshing mid-game (or getting bumped by a phone locking) should drop you
  // straight back into your seat instead of asking for your name again.
  useEffect(() => {
    if (!socket || !hasSeatHere() || hasSetUsername) return;

    let cancelled = false;
    Axios.get(`${baseUrl}/api/isGameStarted/${code}`, { withCredentials: true })
      .then((res) => {
        if (cancelled || !res.data?.started) return;
        setConnectionState("rejoining");
        setHasSetUsername(true);
        socket.emit("identifyPlayer", code, getId(), getSecret());
      })
      .catch((err) => {
        if (err?.response?.status === 404) setLobbyMissing(true);
      });

    return () => {
      cancelled = true;
    };
  }, [socket, code, hasSetUsername]);

  // Phone-sized viewport: used to switch the player popover to a bottom sheet.
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 760
  );

  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 759px)");
    if (!mq) return;

    const update = (e) => setIsCompact(e.matches);
    setIsCompact(mq.matches);
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const MODAL_WIDTH = 250;
  const MODAL_HEIGHT = 210;
  const MODAL_MARGIN = 8;

  const handleOpenModal = (player, event) => {
    // Anchor to the name that was clicked rather than the pointer: the rows
    // are buttons, and activating one from the keyboard reports clientX/Y as
    // 0, which would drop the popup in the corner.
    const rect = event.currentTarget?.getBoundingClientRect?.();
    // Centre it under the name. The row is a full-width button, so hugging its
    // left edge would sit off to one side of the text you actually clicked.
    const anchorLeft = rect
      ? rect.left + rect.width / 2 - MODAL_WIDTH / 2
      : event.clientX;
    const anchorTop = rect ? rect.bottom + 4 : event.clientY;

    const maxLeft = window.innerWidth - MODAL_WIDTH - MODAL_MARGIN;
    const left = Math.max(MODAL_MARGIN, Math.min(anchorLeft, maxLeft));

    // Flip above the name when there isn't room beneath it.
    let top = anchorTop;
    if (top + MODAL_HEIGHT > window.innerHeight - MODAL_MARGIN) {
      top = rect
        ? rect.top - MODAL_HEIGHT - 4
        : window.innerHeight - MODAL_HEIGHT - MODAL_MARGIN;
    }
    top = Math.max(MODAL_MARGIN, top);

    setSelectedPlayer(player);
    setModalStyle({
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      right: "auto",
      bottom: "auto",
      transform: "none",
    });
    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
    setSelectedPlayer(null);
  };

  const handleKick = (p) => {
    // alert(`Player ${p} kicked!`);
    setModalIsOpen(false);

    if (socket) {
      socket.emit("kickPlayer", code, p);
    }
  };

  const setTeam = (playerId, teamIndex) => {
    setModalIsOpen(false);
    socket.emit("setTeam", code, playerId, teamIndex);
  };

  const teamIndexOf = (playerId) =>
    teams.findIndex((t) => t.players.some((p) => p.id === playerId));

  // Moving someone flips them to the other team; a spectator lands on the
  // smaller side.
  const otherTeamIndex = (playerId) => {
    const current = teamIndexOf(playerId);
    if (current === 0) return 1;
    if (current === 1) return 0;
    return teams[0]?.players.length <= teams[1]?.players.length ? 0 : 1;
  };

  const changeTeam = () => {
    const id = getId();
    if (!id || !socket) return;
    socket.emit("setTeam", code, id, otherTeamIndex(id));
  };

  const spectate = () => {
    const id = getId();
    if (!id || !socket) return;
    socket.emit("setTeam", code, id, null);
  };

  const joinTeam = (teamIndex) => {
    const id = getId();
    if (!id || !socket) return;
    socket.emit("setTeam", code, id, teamIndex);
  };

  const amSpectating = () => spectators.some((p) => p.id === getId());

  const getId = () => localStorage.getItem("outlawId");

  // Proves this browser owns its seat when rejoining a game in progress.
  const getSecret = () => localStorage.getItem("outlawSecret");

  // The seat is only ours if it was issued for *this* lobby.
  const hasSeatHere = () =>
    localStorage.getItem("outlawLobby") === code && !!getId() && !!getSecret();

  const clearSeat = () => {
    localStorage.removeItem("outlawId");
    localStorage.removeItem("outlawSecret");
    localStorage.removeItem("outlawLobby");
  };

  const leaveGame = () => {
    clearSeat();
    window.location.href = "/";
  };

  // This used to be called from inside the JSX, firing a request on every
  // single render.
  useEffect(() => {
    if (isGameStarted || !hasSetUsername) return;

    Axios.get(`${baseUrl}/api/lobbySettings/${code}`, {
      withCredentials: true,
    })
      .then(function (res) {
        setSettings({
          pack: res.data?.lobbyPackSelected ?? "General",
          time: res.data?.lobbyTimeSelected ?? "90",
          losePoint: res.data?.lobbyLosePointSelected ?? "Yes",
        });
      })
      .catch(function (err) {
        if (err?.response?.status === 404) setLobbyMissing(true);
      });
  }, [code, isGameStarted, hasSetUsername]);

  const handlePackSelectChange = (e) => {
    const value = e.target.value;
    setSettings((s) => ({ ...s, pack: value }));
    socket.emit("packSelected", value, code);
  };

  const handleControlsBoxSelectChange = (e) => {
    const value = e.target.value;
    setSettings((s) => ({ ...s, time: value }));
    socket.emit("timeSelected", value, code);
  };

  const handleLosePointSelectChange = (e) => {
    const value = e.target.value;
    setSettings((s) => ({ ...s, losePoint: value }));
    socket.emit("losePointSelected", value, code);
  };

  const amISpeaker = (player) => {
    if (!player) {
      player = speaker;
    }
    return player?.id === getId();
  };

  const amIOtherTeam = () => {
    return otherTeam.some((p) => p.id === getId());
  };

  useEffect(() => {
    Axios.get(`${baseUrl}/api/wordlistLengths`, { withCredentials: true })
      .then(function (res) {
        setWordlists(res.data?.counts);
        // console.log(res.data?.counts);
      })
      .catch(function (err) {
        console.log("error in fetching wordlists", err);
      });
  }, []);

  useEffect(() => {
    let gameSocket = io(`${baseUrl}/${code}`, {
      withCredentials: true,
      // Keep trying, and don't back off so far that coming out of a tunnel
      // means waiting half a minute to get back into the game.
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 4000,
      timeout: 8000,
    });
    setSocket(gameSocket);

    return () => {
      if (gameSocket) {
        gameSocket.disconnect();
      }
    };
  }, [code]);

  useEffect(() => {
    if (socket !== null) {
      // socket.io restores the transport by itself, but the server still has
      // us marked disconnected until we prove who we are again. Without this a
      // few seconds of bad signal cost you the rest of the game: your turns get
      // skipped and none of your taps do anything.
      socket.on("connect", () => {
        if (hasSeatHere()) {
          setConnectionState("rejoining");
          socket.emit("identifyPlayer", code, getId(), getSecret());
        } else {
          setConnectionState("connected");
        }
      });

      socket.on("gameStarted", (wordPack, secondsPerRound, totalRounds) => {
        setIsGameStarted(true);
        setStartError("");
        setRoundsToComplete(totalRounds);
        setTimePerRound(secondsPerRound);
        setCategory(wordPack);
      });

      socket.on("wordIs", (word, playersWhoCantSeeWord = []) => {
        setCanSeeWord(!playersWhoCantSeeWord.includes(getId()));
        setWord(word?.word ?? null);
        setBannedWords(word?.bannedWords ?? []);
      });

      socket.on("reconnectInfo", (player, roundStarted) => {
        setConnectionState("connected");
        setIsRoundStarted(roundStarted);
        setGameStartedLockout(false);
        setIsGameStarted(true);
        setHasSetUsername(true);
        setUsername(player.username);
      });

      socket.on("gameStateIs", (lobby) => {
        // console.log(lobby);
      });

      socket.on("playerKicked", (player) => {
        if (player.id === getId()) {
          localStorage.removeItem("outlawId");
          localStorage.removeItem("outlawSecret");
          window.location.href = "/";
        }
      });

      socket.on("setMissed", (amount) => {
        // console.log("Incrementing missed by", amount);
        setMissed(amount);
      });

      socket.on("setGuessed", (amount) => {
        // console.log("Incrementing guessed by", amount);
        setGuessed(amount);
      });

      socket.on("setPackBoxValue", (val) => {
        setSettings((s) => ({ ...s, pack: val }));
      });

      socket.on("setTimeBoxValue", (val) => {
        setSettings((s) => ({ ...s, time: val }));
      });

      socket.on("setLosePointBoxValue", (val) => {
        setSettings((s) => ({ ...s, losePoint: val }));
      });

      // The server tells us the lobby is gone rather than silently throwing.
      socket.on("lobbyGone", () => {
        setLobbyMissing(true);
      });

      socket.on("startGameRejected", (reason) => {
        setStartError(reason || "Could not start the game.");
      });

      socket.on("identifyFailed", () => {
        // The seat is not ours (or no longer exists) - stop pretending it is
        // and come in as a newcomer rather than hitting a dead end.
        clearSeat();
        setConnectionState("connected");
        const name = (getUsernameCache() || "").trim();
        if (name) socket.emit("setUsername", name);
      });

      // The turn counter is the server's to keep; the client used to
      // increment its own copy in two places plus a fudge on reconnect.
      socket.on("setTurn", (completed, total) => {
        setRoundsCompleted(completed);
        setRoundsToComplete(total);
      });

      socket.on("gameReset", () => {
        setIsGameEnded(false);
        setIsGameStarted(false);
        setIsRoundStarted(false);
        setWord(null);
        setBannedWords([]);
        setGuessed(0);
        setMissed(0);
        setPostRoundString("");
        setStartError("");
      });

      socket.on("gameEnded", (individualScores_) => {
        setIndividualScores(
          Array.isArray(individualScores_) ? individualScores_ : []
        );
        setIsGameEnded(true);
      });

      socket.on("roundEnded", (speaker_, roundScore) => {
        setPostRoundString(
          `${speaker_?.username ?? "That round"} scored ${roundScore} ${
            Math.abs(roundScore) === 1 ? "point" : "points"
          }!`
        );
        setIsRoundStarted(false);
      });

      socket.on("roundStarted", () => {
        setGuessed(0);
        setMissed(0);
        setPostRoundString("");
        setIsRoundStarted(true);
      });

      socket.on("outlawCalled", (speaker_) => {
        // console.log("Outlaw called");
        // console.log(speaker_);
        // console.log(username);
        if (amISpeaker(speaker_) && localStorage.getItem("outlawMuted") !== "1") {
          let a = new Audio("/buzzer.mp3");
          a.volume = 0.15;
          a.play().catch(() => {});
        }
      });

      socket.on("setSpeaker", (speaker, teamNotPlaying, teamPlaying) => {
        // console.log("Speaker is", speaker);
        // console.log("Am I speaker?", amISpeaker(speaker));

        // console.log("Not playing:", teamNotPlaying.players);
        setOtherTeam(teamNotPlaying.players);
        setSpeaker(speaker);
        // console.log("Setting speakerTeam to", teamPlaying);
        setSpeakerTeam(teamPlaying);
      });

      socket.on("chatMessage", (message, author, color = "#FFF") => {
        // console.log("Chat message received", message, author);
        setChatMessages((chatMessages) =>
          [
            ...chatMessages,
            { author: author.username, content: message, color: color },
          ].slice(-50)
        );
      });

      socket.on("setId", (id, secret) => {
        localStorage.setItem("outlawId", id);
        localStorage.setItem("outlawLobby", code);
        if (secret) localStorage.setItem("outlawSecret", secret);
      });

      socket.on("updateTeams", (newTeams, newSpectators = []) => {
        setTeams(newTeams);
        setSpectators(newSpectators);
      });

      socket.on("joinedAsSpectator", () => {
        setIsGameStarted(true);
        setHasSetUsername(true);
        setGameStartedLockout(false);
      });

      socket.on("actionRejected", (reason) => {
        setActionError(reason || "");
        setTimeout(() => setActionError(""), 2500);
      });

      socket.on("systemChatMessageRed", (message, userToImpersonate) => {
        // console.log("System chat message received", message);
        setChatMessages((chatMessages) =>
          [
            ...chatMessages,
            { author: userToImpersonate, content: message, systemType: "red" },
          ].slice(-50)
        );
      });

      socket.on("systemChatMessageGreen", (message, userToImpersonate) => {
        // console.log("System chat message received", message);
        setChatMessages((chatMessages) =>
          [
            ...chatMessages,
            {
              author: userToImpersonate,
              content: message,
              systemType: "green",
            },
          ].slice(-50)
        );
      });

      socket.on("systemChatMessageBlue", (message, userToImpersonate) => {
        // console.log("System chat message received", message);
        setChatMessages((chatMessages) =>
          [
            ...chatMessages,
            { author: userToImpersonate, content: message, systemType: "blue" },
          ].slice(-50)
        );
      });

      socket.on("disconnect", () => {
        setConnectionState("reconnecting");
      });
    }

    // Without this every remount stacked another full set of listeners.
    return () => {
      if (socket) socket.removeAllListeners();
    };
  }, [socket]);

  const addChatMessage = (message, author) => {
    if (socket) {
      socket.emit("addChatMessage", message, getId(), code);
    }
  };

  const checkIfGameAlreadyStarted = async () => {
    try {
      const res = await Axios.get(`${baseUrl}/api/isGameStarted/${code}`, {
        withCredentials: true,
      });
      setGameStartedLockout(res.data?.started);
      return res.data?.started;
    } catch (err) {
      // This request used to hang forever for an unknown lobby, so the Join
      // button silently did nothing. Now the server 404s and we say so.
      if (err?.response?.status === 404) {
        setLobbyMissing(true);
        return null;
      }
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!socket) return;

    const name = username.trim();
    if (!name) return;

    checkIfGameAlreadyStarted().then((gameStarted) => {
      if (gameStarted === null) return; // lobby doesn't exist

      setGameStartedLockout(false);
      setHasSetUsername(true);
      setUsernameCache(name);

      // Reclaim a seat we already hold here; otherwise join - the server puts
      // you with the spectators if a game is already running.
      if (gameStarted && hasSeatHere()) {
        socket.emit("identifyPlayer", code, getId(), getSecret());
      } else {
        socket.emit("setUsername", name);
      }
    });
  };

  const cancelSubmit = (e) => {
    e.preventDefault();
  };

  // The early return here meant the field could never be cleared.
  const handleChange = (e) => {
    setUsername(e.target.value.slice(0, 20));
  };

  const startGame = () => {
    setStartError("");
    socket.emit(
      "startGame",
      code,
      settings.pack,
      parseInt(settings.time, 10),
      settings.losePoint === "Yes"
    );
  };

  const playAgain = () => {
    socket.emit("playAgain", code);
  };

  // Hand the turn to the next player without playing it.
  const skipTurn = () => {
    socket.emit("skipTurn", code);
  };

  const copyInvite = () => {
    const link = window.location.href;
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    };

    // navigator.clipboard needs a secure context; fall back so the button is
    // never a no-op that silently does nothing.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(done).catch(fallbackCopy);
      return;
    }
    fallbackCopy();

    function fallbackCopy() {
      try {
        const field = document.createElement("textarea");
        field.value = link;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        document.execCommand("copy");
        document.body.removeChild(field);
        done();
      } catch {
        window.prompt("Copy this link:", link);
      }
    }
  };

  const toggleMute = () => {
    setIsMuted((muted) => {
      const next = !muted;
      localStorage.setItem("outlawMuted", next ? "1" : "0");
      return next;
    });
  };

  // The server picks and delivers the next word itself now - these used to
  // race when two players clicked at the same moment.
  const correctAnswer = () => socket.emit("correctAnswer");

  const pass = () => socket.emit("pass");

  const callOutlaw = () => socket.emit("callOutlaw");

  // Compared usernames before, so two players with the same name both got
  // leader powers.
  const leader = () => {
    for (const team of teams) {
      const found = team.players.find((p) => p.isLeader);
      if (found) return found;
    }
    return null;
  };

  // Mirrors the server's own check, so the button explains itself instead of
  // being rejected after the fact.
  const canStart = () =>
    teams.length === 2 && teams.every((t) => t.players.length > 0);

  const amILeader = () => {
    const id = getId();
    return teams.some((team) =>
      team.players.some((player) => player.id === id && player.isLeader)
    );
  };

  const startRound = () => {
    socket.emit("startRound", code);
  };

  const solidify = (colour) =>
    colour.replace(/rgba\((\d+), (\d+), (\d+), [\d.]+\)/, "rgb($1, $2, $3)");

  const setColourPreference = (colours, pattern) => {
    // console.log("Setting colour preference", colours, pattern);
    localStorage.setItem("outlawColours", colours);
    localStorage.setItem("outlawPattern", pattern);

    if (colours) {
      document.documentElement.style.setProperty(
        "--background-color",
        colours[0]
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        colours[1]
      );
      document.documentElement.style.setProperty(
        "--tertiary-color",
        colours[2]
      );
      document.documentElement.style.setProperty(
        "--text-on-bg-color",
        colours[3]
      );

      const colorParts = colours[1].match(/\d+/g);
      if (colorParts && colorParts.length >= 4) {
        const halfOpacity = 1;
        const halfOpacityColor = `rgba(${colorParts[0]}, ${colorParts[1]}, ${colorParts[2]}, ${halfOpacity})`;

        localStorage.setItem("outlawBackgroundColourHalfTp", halfOpacityColor);
        document.documentElement.style.setProperty(
          "--background-color-full-tp",
          halfOpacityColor
        );
      }
    }

    if (pattern) {
      document.documentElement.style.setProperty(
        "--background-pattern",
        pattern
      );
    } else {
      document.documentElement.style.removeProperty("--background-pattern");
    }
  };

  const applyTheme = (theme) => {
    setActiveTheme(theme.id);
    localStorage.setItem("outlawThemeId", theme.id);
    setColourPreference(theme.colours, theme.pattern);

    // Keep the browser chrome (status bar, tab strip) in step with the theme.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", solidify(theme.colours[0]));
  };

  const setUsernameCache = (u) => {
    localStorage.setItem("outlawUsernameCache", u);
  };

  const getUsernameCache = () => {
    return localStorage.getItem("outlawUsernameCache");
  };

  const screen = lobbyMissing ? (
    <div className="lobby-container">
      <div
        className="lobby"
        style={{ display: "flex", alignItems: "center", textAlign: "center" }}
      >
        <div style={{ margin: "0 auto" }}>
          <h1 style={{ color: "#ec596a", margin: 0 }}>Lobby not found</h1>
          <p style={{ opacity: 0.7 }}>
            This game has finished or the code is wrong.
          </p>
          <button id="create-game" onClick={() => (window.location.href = "/")}>
            Back to home
          </button>
        </div>
      </div>
    </div>
  ) : isGameStarted ? (
    // Game
    <>
      {bannedWords && (
        teams && teams[0] && teams[1] && (
          <div className="game-screen">
          {connectionState !== "connected" && (
            <div className="connection-banner" role="status">
              {connectionState === "reconnecting"
                ? "Connection lost - hang on, reconnecting..."
                : "Getting you back in..."}
            </div>
          )}
          <div className="game-header">
          {!isGameEnded && (
          <div className="round-details">
            <div>
              <div>
                <h4 style={{ margin: "0" }}>Guessed</h4>
                <h4 className="second" style={{ margin: "0" }}>
                  {guessed}
                </h4>
              </div>
              <div>
                <Timer initialTime={timePerRound} socket={socket} />
              </div>
              <div>
                <h4 style={{ margin: "0" }}>Missed</h4>
                <h4 className="second" style={{ margin: "0" }}>
                  {missed}
                </h4>
              </div>
            </div>
          </div>
          )}
          <p className="turn-line">
            <span>
              {isGameEnded
                ? "Final score!"
                : `Turn ${Math.min(
                    roundsCompleted + 1,
                    roundsToComplete
                  )} of ${roundsToComplete}`}
            </span>
            <button
              type="button"
              className="link-button"
              onClick={toggleMute}
              aria-pressed={isMuted}
            >
              {isMuted ? "Unmute" : "Mute"}
            </button>
            <button type="button" className="link-button" onClick={leaveGame}>
              Leave
            </button>
          </p>
          </div>

          <div className="game">
            <BanneredDiv
              cls="scoreboard"
              header="Score"
              content={
                <div className="roster">
                  <div className="roster-lists">
                  {teams.map((team, index) => (
                    <div
                      className="roster-team"
                      key={team.name}
                      style={{
                        "--team-tint":
                          index === 0 ? "var(--team-green)" : "var(--team-blue)",
                      }}
                    >
                      <div className="roster-head">
                        <span className={index === 0 ? "green" : "blue"}>
                          <FontAwesomeIcon
                            icon={index === 0 ? faLeaf : faDroplet}
                            className="icon-pad-right"
                          />
                          <strong>{team.name}</strong>
                        </span>
                        <strong>{team.score} pts</strong>
                      </div>

                      {team.players.length === 0 && (
                        <p className="roster-empty">Nobody yet</p>
                      )}

                      <div className="roster-players">
                      {team.players.map((player) => (
                        <button
                          type="button"
                          key={player.id}
                          className={`roster-player${
                            player.isConnected === false ? " player-offline" : ""
                          }${player.id === speaker?.id ? " roster-player--speaking" : ""}`}
                          onClick={(event) => handleOpenModal(player, event)}
                        >
                          {player.isLeader && (
                            <FontAwesomeIcon
                              icon={faCrown}
                              className="icon-pad-right"
                            />
                          )}
                          {player.username}
                          {player.isConnected === false && " (offline)"}
                        </button>
                      ))}
                      </div>
                    </div>
                  ))}

                  {spectators.length > 0 && (
                    <div className="roster-team">
                      <div className="roster-head">
                        <span className="roster-watching">
                          <FontAwesomeIcon
                            icon={faEye}
                            className="icon-pad-right"
                          />
                          <strong>Spectators</strong>
                        </span>
                      </div>
                      <div className="roster-players">
                      {spectators.map((player) => (
                        <button
                          type="button"
                          key={player.id}
                          className={`roster-player${
                            player.isConnected === false ? " player-offline" : ""
                          }`}
                          onClick={(event) => handleOpenModal(player, event)}
                        >
                          {player.isLeader && (
                            <FontAwesomeIcon
                              icon={faCrown}
                              className="icon-pad-right"
                            />
                          )}
                          {player.username}
                          {player.isConnected === false && " (offline)"}
                        </button>
                      ))}
                      </div>
                    </div>
                  )}

                  </div>

                  <div className="roster-actions">
                    {amSpectating() ? (
                      <>
                        <button
                          type="button"
                          className="roster-action"
                          onClick={() => joinTeam(0)}
                        >
                          Join {teams[0].name}
                        </button>
                        <button
                          type="button"
                          className="roster-action"
                          onClick={() => joinTeam(1)}
                        >
                          Join {teams[1].name}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="roster-action"
                          onClick={changeTeam}
                        >
                          Switch team
                        </button>
                        <button
                          type="button"
                          className="roster-action"
                          onClick={spectate}
                        >
                          Spectate
                        </button>
                      </>
                    )}
                  </div>

                  {actionError && (
                    <p className="form-error" role="alert">
                      {actionError}
                    </p>
                  )}
                </div>
              }
              contentStyle={{ borderRadius: "0px 0px 4px 4px" }}
              headerStyle={{
                marginTop: "1em",
                borderRadius: "4px 4px 0px 0px",
              }}
              after={
                // "X is up next" is nonsense once the game has finished.
                !isGameEnded && (
                  <div className="speaker-line">
                    {speaker && speakerTeam && (
                      <p>
                        <strong style={{ color: speakerTeam.colour }}>
                          {speaker.username}
                        </strong>{" "}
                        {isRoundStarted ? "is speaking!" : "is up next!"}
                      </p>
                    )}
                  </div>
                )
              }
            />
            {isGameEnded ? (
              <BanneredDiv
                cls="words"
                header="GAME OVER"
                content={
                  <>
                    <WinningMessage
                      teams={teams}
                      username={username}
                      individualScores={individualScores}
                    />
                  </>
                }
                headerStyle={{ marginTop: "0.3em" }}
                after={
                  amILeader() ? (
                    <span className="speaker-buttons">
                      <button className="success" onClick={playAgain}>
                        Play again
                      </button>
                    </span>
                  ) : (
                    <span className="speaker-buttons">
                      <p style={{ margin: "0 auto", opacity: 0.6 }}>
                        Waiting for the leader to start a new game...
                      </p>
                    </span>
                  )
                }
              />
            ) : isRoundStarted ? (
              canSeeWord ? (
                <BanneredDiv
                  cls="words"
                  header={word?.toUpperCase()}
                  content={
                    <>
                      {bannedWords.map((word, index) => (
                        <div className="banned-word" key={index}>
                          {word}
                        </div>
                      ))}
                    </>
                  }
                  headerStyle={{ marginTop: "0.3em" }}
                  after={
                    !amIOtherTeam() ? (
                      <span className="speaker-buttons">
                        <button className="fail" onClick={pass}>
                          Pass
                        </button>
                        <button className="success" onClick={correctAnswer}>
                          Got it!
                        </button>
                      </span>
                    ) : (
                      <span className="speaker-buttons">
                        <button className="fail" onClick={callOutlaw}>
                          Call Outlaw!
                        </button>
                      </span>
                    )
                  }
                />
              ) : (
                <BanneredDiv
                  cls="words words--tall"
                  header="Guess!"
                  content={
                    <div className="banned-word">
                      {speaker.username} is describing a word!
                    </div>
                  }
                  headerStyle={{ marginTop: "0.3em" }}
                />
              )
            ) : amISpeaker() ? (
              <BanneredDiv
                cls="words words--tall"
                header="YOUR TURN!"
                content={
                  <div className="start-turn-wrap">
                    <button
                      className={`start ${speakerTeam.name.toLowerCase()}-dark-border`}
                      onClick={startRound}
                      style={{
                        backgroundColor: speakerTeam.colour,
                        color: "#333",
                      }}
                    >
                      Start turn
                    </button>
                    <button className="skip-turn" onClick={skipTurn}>
                      Skip my turn
                    </button>
                  </div>
                }
                headerStyle={{ marginTop: "0.3em" }}
              />
            ) : (
              <BanneredDiv
                cls="words words--tall"
                header="WAITING..."
                content={
                  <>
                    {postRoundString && (
                      <div className="banned-word last-round">
                        {postRoundString}
                      </div>
                    )}
                    <div className="banned-word">
                      Waiting for {speaker.username} to start the round!
                    </div>
                  </>
                }
                headerStyle={{ marginTop: "0.3em" }}
              />
            )}

            <BanneredDiv
              cls="chat"
              header="Chat"
              content={
                <ChatBox
                  addChatMessage={addChatMessage}
                  chatMessages={chatMessages}
                  username={username}
                />
              }
              contentStyle={{ borderRadius: "0px 0px 4px 4px" }}
              headerStyle={{
                marginTop: "1em",
                borderRadius: "4px 4px 0px 0px",
              }}
            />
          </div>
          </div>
        )
      )}
    </>
  ) : gameStartedLockout ? (
    <div className="lobby-container">
      <div
        className="lobby"
        style={{ display: "flex", alignItems: "center", textAlign: "center" }}
      >
        <div style={{ margin: "0 auto" }}>
          <h1 style={{ color: "#ec596a", margin: 0 }}>
            Game has already started
          </h1>
          <button id="create-game" onClick={() => (window.location.href = "/")}>
            Back to home
          </button>
        </div>
      </div>
    </div>
  ) : teams.length > 0 && hasSetUsername ? (
    <div className="lobby-container settings">
      <div className="lobby teams">
        <form onSubmit={cancelSubmit}>
          <div className="form-content">
            {/* The code is how people get in - it should not vanish the
                moment you join. */}
            <div className="lobby-code">
              <span className="lobby-code-label">Lobby code</span>
              <strong className="lobby-code-value">{code}</strong>
              <button
                type="button"
                className="link-button"
                onClick={copyInvite}
              >
                {copied ? "Link copied!" : "Copy invite link"}
              </button>
            </div>
            <div className="teams-container">
              <div className="teams-box top" style={{ color: teams[0].colour }}>
                <h2>
                  <FontAwesomeIcon icon={faLeaf} className="icon-pad-right" />
                  {teams[0].name}
                </h2>
              </div>
              <div className="teams-box top" style={{ color: teams[1].colour }}>
                <h2>
                  <FontAwesomeIcon
                    icon={faDroplet}
                    className="icon-pad-right"
                  />
                  {teams[1].name}
                </h2>
              </div>
              <div className="teams-box">
                {teams[0].players.map((player) => (
                  <div key={player.id}>
                    <button
                      type="button"
                      className={`player${
                        player.isConnected === false ? " player-offline" : ""
                      }`}
                      onClick={(event) => handleOpenModal(player, event)}
                    >
                      {player.isLeader && (
                        <FontAwesomeIcon
                          icon={faCrown}
                          className="icon-pad-right"
                        />
                      )}
                      {player.username}
                      {player.isConnected === false && " (offline)"}
                    </button>
                  </div>
                ))}
              </div>
              <div className="teams-box">
                {teams[1].players.map((player) => (
                  <div key={player.id}>
                    <button
                      type="button"
                      className={`player${
                        player.isConnected === false ? " player-offline" : ""
                      }`}
                      onClick={(event) => handleOpenModal(player, event)}
                    >
                      {player.isLeader && (
                        <FontAwesomeIcon
                          icon={faCrown}
                          className="icon-pad-right"
                        />
                      )}
                      {player.username}
                      {player.isConnected === false && " (offline)"}
                    </button>
                  </div>
                ))}
              </div>

            </div>
          </div>
          {spectators.length > 0 && (
            <div className="lobby-spectators">
              <span className="lobby-spectators-label">Spectators</span>
              {spectators.map((player) => (
                <button
                  type="button"
                  key={player.id}
                  className="player"
                  onClick={(event) => handleOpenModal(player, event)}
                >
                  {player.username}
                </button>
              ))}
            </div>
          )}
          <div className="controls-container">
            <div className="lobby-team-actions">
              {amSpectating() ? (
                <>
                  <button
                    type="button"
                    className="roster-action"
                    onClick={() => joinTeam(0)}
                  >
                    Join {teams[0].name}
                  </button>
                  <button
                    type="button"
                    className="roster-action"
                    onClick={() => joinTeam(1)}
                  >
                    Join {teams[1].name}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="roster-action"
                    onClick={changeTeam}
                  >
                    Change team
                  </button>
                  <button
                    type="button"
                    className="roster-action"
                    onClick={spectate}
                  >
                    Spectate
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
      <div className="lobby buttons">
        <form onSubmit={cancelSubmit}>
          <p
            className="label"
            style={{ opacity: `${!amILeader() ? "50%" : "100%"}` }}
          >
            Word pack
          </p>
          <div
            className="pack-selection"
            style={{ alignContent: "left", textAlign: "left" }}
          >
            {wordlists && (
              <select
                id="pack-select"
                aria-label="Word pack"
                onChange={handlePackSelectChange}
                value={settings.pack}
                disabled={!amILeader()}
              >
                {Object.entries(wordlists).map(
                  ([listName, listCount], index) => (
                    <option key={index} value={listName}>
                      {listName} ({listCount} words)
                    </option>
                  )
                )}
              </select>
            )}
          </div>
          <div className="controls-box">
            <div className="control">
              <p
                className="label"
                style={{ opacity: `${!amILeader() ? "50%" : "100%"}` }}
              >
                Time
              </p>
              <select
                id="time-select"
                aria-label="Seconds per turn"
                onChange={handleControlsBoxSelectChange}
                value={settings.time}
                disabled={!amILeader()}
              >
                <option value="10">10 seconds</option>
                <option value="60">60 seconds</option>
                <option value="70">70 seconds</option>
                <option value="80">80 seconds</option>
                <option value="90">90 seconds</option>
                <option value="100">100 seconds</option>
                <option value="110">110 seconds</option>
                <option value="120">120 seconds</option>
              </select>
            </div>
            <div className="control">
              <p
                className="label"
                style={{ opacity: `${!amILeader() ? "50%" : "100%"}` }}
              >
                Lose point on fail
              </p>
              <select
                id="lose-point-select"
                aria-label="Lose a point on fail"
                onChange={handleLosePointSelectChange}
                value={settings.losePoint}
                disabled={!amILeader()}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>
          <div className="controls-container">
            <button
              id="start-game"
              onClick={startGame}
              disabled={!amILeader() || !canStart()}
            >
              Start
            </button>
            {!amILeader() ? (
              <p className="hint">
                Waiting for {leader()?.username || "the leader"} to start!
              </p>
            ) : !canStart() ? (
              <p className="hint">Both teams need at least one player.</p>
            ) : null}
            {startError && (
              <p className="form-error" role="alert">
                {startError}
              </p>
            )}
          </div>

        </form>
      </div>
    </div>
  ) : (
    <>
      <div className="lobby-container">
        <div className="lobby">
          <form onSubmit={handleSubmit}>
            <div className="form-content">
              <h1 style={{ margin: 0 }}>Outlaw - {code}</h1>
              <button type="button" className="link-button" onClick={copyInvite}>
                {copied ? "Link copied!" : "Copy invite link"}
              </button>
              <p className="label">Your name</p>
              <br />
              <input
                type="text"
                onChange={handleChange}
                placeholder="Enter your name"
                aria-label="Your name"
                maxLength={20}
                autoComplete="nickname"
                defaultValue={getUsernameCache() || ""}
              />
              <br />
              <p className="label">Theme</p>
              <br />
              <div className="colour-container">
                {themes.map((theme) => (
                  <ColorPicker
                    key={theme.id}
                    colours={theme.colours}
                    pattern={theme.pattern}
                    thumbnailColourOverride={theme.thumbnail}
                    label={theme.label}
                    isSelected={activeTheme === theme.id}
                    setColourPreference={() => applyTheme(theme)}
                  />
                ))}
              </div>

              <br />
            </div>
            <button
              style={{ margin: "10px 0px 0px 0px" }}
              type="submit"
              onClick={null}
              disabled={!socket || username.trim().length < 1}
              id="join-game"
            >
              Join
            </button>
          </form>
        </div>
      </div>
    </>
  );

  // One menu for both screens: the lobby list and the in-game roster.
  return (
    <>
      {screen}
      {selectedPlayer && (
        <PlayerControlsModal
          isOpen={modalIsOpen}
          onRequestClose={handleCloseModal}
          onKick={handleKick}
          onSetTeam={setTeam}
          teams={teams}
          currentTeamIndex={teamIndexOf(selectedPlayer.id)}
          otherTeamIndex={otherTeamIndex(selectedPlayer.id)}
          player={selectedPlayer}
          modalStyle={modalStyle}
          amILeader={amILeader}
          isCompact={isCompact}
          className="modal"
        />
      )}
    </>
  );
};

export default Game;
